// DNE / DNC from Emails (/admin/opt-outs, admin only, 2026-10-19): drag emails
// out of Outlook, review every email address / phone number found in them, then
// add the ticked ones to the two global compliance lists — DNE ("Global
// DemandArc") and DNC ("Global DemandArc DNC"). Its own page on purpose; List
// Management's generic list editor is untouched. Backend: /global-opt-outs.
//
// Nothing is saved on drop. Every candidate starts ticked except ones already
// covered, because a forwarded email's signature carries our own phone number
// and only a person can tell which number is the requester's.
import React, { useState } from 'react';
import axios from 'axios';
import FileDropZone from '../components/FileDropZone';

const API = process.env.REACT_APP_API_URL;

const btn = (color, disabled) => ({
  padding: '7px 16px', background: disabled ? '#bdc3c7' : color, color: '#fff', border: 'none',
  borderRadius: '4px', cursor: disabled ? 'default' : 'pointer', fontSize: '13px', fontWeight: '600',
});
const tag = (bg, fg) => ({
  fontSize: '11px', padding: '1px 7px', borderRadius: '10px', background: bg, color: fg,
  marginLeft: '6px', whiteSpace: 'nowrap',
});

export function formatPhone(digits) {
  return digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : `+${digits}`;
}

const keyOf = (kind, value) => `${kind}:${value}`;

// Unique candidates across every dropped message, first-seen order, with the
// "already covered" reason the backend attached. Exported for tests.
export function collectCandidates(messages) {
  const seen = new Map();
  for (const m of messages) {
    for (const e of m.emails || []) {
      const k = keyOf('email', e.value);
      if (!seen.has(k)) seen.set(k, { kind: 'email', ...e });
    }
    for (const p of m.phones || []) {
      const k = keyOf('phone', p.value);
      if (!seen.has(k)) seen.set(k, { kind: 'phone', ...p });
    }
  }
  return seen;
}

export default function GlobalOptOuts() {
  const [messages, setMessages] = useState([]);
  const [checked, setChecked] = useState({});
  const [lists, setLists] = useState(null);
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const candidates = collectCandidates(messages);
  const picked = [...candidates.entries()].filter(([k, c]) => !c.existing && checked[k]);
  const pickedEmails = picked.filter(([, c]) => c.kind === 'email').map(([, c]) => c.value);
  const pickedPhones = picked.filter(([, c]) => c.kind === 'phone').map(([, c]) => c.value);

  const extract = async (files) => {
    setBusy(true); setError(''); setResult(null);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f, f.name));
      const res = await axios.post(`${API}/global-opt-outs/extract`, fd);
      setLists({ dne: res.data.dne_list, dnc: res.data.dnc_list });
      const fresh = res.data.messages;
      setMessages(prev => [...prev, ...fresh]);
      setChecked(prev => {
        const next = { ...prev };
        for (const [k, c] of collectCandidates(fresh)) {
          if (!(k in next)) next[k] = !c.existing;
        }
        return next;
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not read those emails');
    } finally {
      setBusy(false);
    }
  };

  const extractPasted = () => {
    if (!pasted.trim()) return;
    extract([new File([pasted], 'Pasted text.txt', { type: 'text/plain' })]);
    setPasted('');
  };

  const add = async () => {
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${API}/global-opt-outs/add`, { emails: pickedEmails, phones: pickedPhones });
      setResult(res.data);
      setMessages([]); setChecked({});
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not add to the lists');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (k) => setChecked(prev => ({ ...prev, [k]: !prev[k] }));

  const renderRow = (kind, item) => {
    const k = keyOf(kind, item.value);
    const label = kind === 'email' ? item.value : formatPhone(item.value);
    return (
      <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0',
        color: item.existing ? '#999' : '#222', cursor: item.existing ? 'default' : 'pointer' }}>
        <input type="checkbox" disabled={!!item.existing} checked={!item.existing && !!checked[k]}
          onChange={() => toggle(k)} />
        <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{label}</span>
        {kind === 'email'
          ? <span style={tag('#fdecea', '#c0392b')}>DNE</span>
          : <span style={tag('#fef5e7', '#b9770e')}>DNC</span>}
        {item.existing && <span style={{ fontSize: '11px', fontStyle: 'italic' }}>{item.existing}</span>}
      </label>
    );
  };

  return (
    <div style={{ maxWidth: '820px' }}>
      <h2 style={{ margin: '0 0 6px' }}>DNE / DNC from Emails</h2>
        <p style={{ margin: '0 0 14px', color: '#666' }}>
          Every email address found goes to <b>{lists?.dne.list_name || 'Global DemandArc'}</b> (DNE); every phone
          number to <b>{lists?.dnc.list_name || 'Global DemandArc DNC'}</b> (DNC). DemandArc and ITBusinessWire addresses are skipped.
          Untick anything that shouldn't be added — e.g. a phone number from our own signature.
        </p>

        {result && (
          <div style={{ background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: '6px', padding: '10px 14px', marginBottom: '14px' }}>
            <b>Added {result.dne_added.length} to DNE and {result.dnc_added.length} to DNC.</b>
            {(result.dne_skipped.length + result.dnc_skipped.length) > 0 && (
              <span> {result.dne_skipped.length + result.dnc_skipped.length} were already on a list.</span>
            )}
            {result.invalid.length > 0 && (
              <div style={{ color: '#c0392b', marginTop: '4px' }}>Not added (invalid): {result.invalid.join(', ')}</div>
            )}
            {[...result.dne_added, ...result.dnc_added.map(formatPhone)].length > 0 && (
              <div style={{ color: '#555', marginTop: '4px', fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                {[...result.dne_added, ...result.dnc_added.map(formatPhone)].join(' · ')}
              </div>
            )}
          </div>
        )}

        <FileDropZone
          label={busy ? 'Reading…' : 'Drop emails here (several at once is fine)'}
          hint="Drag messages from Outlook, or click to pick .eml files. If Outlook won't drop here, drag them to your Desktop first."
          accept=".eml,.txt,.msg,message/rfc822"
          onFiles={extract}
          disabled={busy}
          ariaLabel="Drop emails"
          style={{ padding: '22px 16px' }}
        />

        <details style={{ margin: '10px 0 4px' }}>
          <summary style={{ cursor: 'pointer', color: '#555' }}>…or paste the email text</summary>
          <textarea value={pasted} onChange={e => setPasted(e.target.value)} rows={4}
            placeholder="Paste an email body, or a list of addresses / numbers"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: '6px', padding: '6px 8px', fontSize: '13px' }} />
          <button onClick={extractPasted} disabled={busy || !pasted.trim()} style={btn('#2c3e50', busy || !pasted.trim())}>
            Find addresses &amp; numbers
          </button>
        </details>

        {error && <div style={{ color: '#c0392b', margin: '10px 0' }}>{error}</div>}

        {messages.length > 0 && (
          <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {messages.map((m, i) => (
              <div key={i} style={{ border: '1px solid #e5e5e5', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontWeight: 600, wordBreak: 'break-word' }}>{m.subject || m.filename}</div>
                {m.sender && <div style={{ color: '#888', fontSize: '12px', wordBreak: 'break-word' }}>From {m.sender}</div>}
                {m.error
                  ? <div style={{ color: '#b9770e', marginTop: '4px' }}>{m.error}</div>
                  : <div style={{ marginTop: '6px' }}>
                      {m.emails.map(e => renderRow('email', e))}
                      {m.phones.map(p => renderRow('phone', p))}
                    </div>}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          {messages.length > 0 && (
            <button onClick={() => { setMessages([]); setChecked({}); }} disabled={busy}
              style={btn('#95a5a6', busy)}>Clear</button>
          )}
          <button onClick={add} disabled={busy || picked.length === 0} style={btn('#c0392b', busy || picked.length === 0)}>
            Add {pickedEmails.length} to DNE · {pickedPhones.length} to DNC
          </button>
        </div>
    </div>
  );
}
